/**
 * PSC Widget - Main Component
 *
 * The main entry point for the PSC widget. Renders the floating button
 * and submission dialog within a provider context.
 */

import { PSCProvider } from './PSCProvider';
import { FloatingButton } from './components/FloatingButton';
import { SubmissionDialog } from './components/SubmissionDialog';
import type { PSCWidgetConfig } from './types';

export interface PSCWidgetProps {
  /** Widget configuration */
  config: PSCWidgetConfig;

  /** Use mock API for development/testing (default: false) */
  useMockApi?: boolean;
}

/**
 * PSC Widget Component
 *
 * Renders a floating action button that opens a dialog for submitting
 * bug reports, feature requests, and other feedback.
 *
 * @example
 * ```tsx
 * import { PSCWidget } from '@design2code/psc-widget';
 * import '@design2code/psc-widget/styles';
 *
 * function App() {
 *   return (
 *     <div>
 *       <YourAppContent />
 *       <PSCWidget
 *         config={{
 *           appName: 'Treasury',
 *           apiBaseUrl: 'http://localhost:8150',
 *           categories: ['Cash Flow', 'Settlements', 'Positions'],
 *           getAuthToken: () => localStorage.getItem('token') || '',
 *           getUserInfo: () => ({
 *             id: user.id,
 *             role: user.role,
 *             email: user.email,
 *           }),
 *         }}
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
export function PSCWidget({ config, useMockApi = false }: PSCWidgetProps) {
  return (
    <PSCProvider config={config} useMockApi={useMockApi}>
      <FloatingButton />
      <SubmissionDialog />
    </PSCProvider>
  );
}
